import { Link } from 'react-router-dom';
import { ROUTES } from '@traction/shared';
import { Check } from 'lucide-react';

const tiers = [
  {
    name: 'Starter',
    price: 49,
    description: 'Perfect for solo founders testing the waters.',
    features: [
      '3 leads per day',
      'AI-drafted emails',
      'Gmail Chrome extension',
      'Lead dashboard',
      'Basic analytics',
      'Email support',
    ],
    cta: 'Start free trial',
    highlighted: false,
  },
  {
    name: 'Growth',
    price: 99,
    description: 'For founders ready to scale their outreach.',
    features: [
      '10 leads per day',
      'AI-drafted emails',
      'Gmail Chrome extension',
      'Lead dashboard',
      'Advanced analytics',
      'Priority support',
      'CSV contact upload',
      'Custom ICP criteria',
      'Reply detection',
    ],
    cta: 'Start free trial',
    highlighted: true,
  },
  {
    name: 'Scale',
    price: 199,
    description: 'For teams with aggressive growth targets.',
    features: [
      '25 leads per day',
      'AI-drafted emails',
      'Gmail Chrome extension',
      'Lead dashboard',
      'Advanced analytics',
      'Priority support',
      'CSV contact upload',
      'Custom ICP criteria',
      'Reply detection',
      'Multiple team members',
      'API access',
      'Dedicated success manager',
    ],
    cta: 'Contact sales',
    highlighted: false,
  },
];

export function PricingPage() {
  return (
    <div className="py-12 bg-cacao-dark">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <h1 className="font-heading text-xl font-semibold text-[#F5F5F5]">Simple, transparent pricing</h1>
          <p className="mt-2 text-sm text-cacao-muted">
            Start free, upgrade when you are ready to scale
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-3">
          {tiers.map((tier) => (
            <div
              key={tier.name}
              className={`rounded-lg border p-5 ${
                tier.highlighted
                  ? 'border-cacao-gold ring-1 ring-cacao-gold bg-cacao-dark-secondary'
                  : 'border-cacao-dark-tertiary bg-cacao-dark-secondary'
              }`}
            >
              {tier.highlighted && (
                <span className="inline-block rounded-full bg-cacao-gold px-2 py-0.5 text-[10px] font-medium text-cacao-dark mb-3">
                  Most Popular
                </span>
              )}
              <h3 className="text-sm font-medium text-[#F5F5F5]">{tier.name}</h3>
              <p className="mt-1 text-xs text-cacao-text-tertiary">{tier.description}</p>
              <div className="mt-3">
                <span className="text-2xl font-semibold text-[#F5F5F5]">${tier.price}</span>
                <span className="text-xs text-cacao-text-tertiary">/month</span>
              </div>
              <ul className="mt-4 space-y-2">
                {tier.features.map((feature) => (
                  <li key={feature} className="flex items-start">
                    <Check className="h-3.5 w-3.5 text-cacao-gold mr-1.5 flex-shrink-0 mt-0.5" />
                    <span className="text-xs text-cacao-muted">{feature}</span>
                  </li>
                ))}
              </ul>
              <Link
                to={tier.cta === 'Contact sales' ? ROUTES.SUPPORT : ROUTES.SIGNUP}
                className={`mt-5 block w-full rounded-md px-3 py-2 text-center text-sm font-medium transition-colors ${
                  tier.highlighted
                    ? 'bg-cacao-gold text-cacao-dark hover:bg-cacao-gold-hover'
                    : 'bg-cacao-dark-tertiary text-[#F5F5F5] hover:bg-cacao-dark-elevated'
                }`}
              >
                {tier.cta}
              </Link>
            </div>
          ))}
        </div>

        <div className="mt-10 text-center">
          <p className="text-xs text-cacao-text-tertiary">
            All plans include a 14-day free trial. No credit card required.
          </p>
          <p className="mt-1.5 text-xs text-cacao-text-tertiary">
            Need a custom plan?{' '}
            <Link to={ROUTES.SUPPORT} className="text-cacao-muted hover:text-white transition-colors">
              Contact us
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
